import { useModalPromise } from '@cp/shared/modal';
import { useCallback, useState } from 'react';
import { AdminFeedbackViewModel } from '@common/model/admin/catalog/feedback/view';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { EMPTY, catchError, defaultIfEmpty, finalize, from, lastValueFrom, switchMap, tap } from 'rxjs';
import { adminCatalogFeedbackApi } from 'src/entities/catalog/feedback';
import { resultFromRTK } from '@cp/shared/form';
import { ApiServerError } from '@cp/utils/rtk/query/model';
import { SerializedError } from '@reduxjs/toolkit';
import { coreModerationApi } from 'src/entities/moderation';
import { useToast } from '@cp/shared/toast';
import { FEEDBACK_EDIT_MODAL } from '../lib/modals';

export type FeedbackEditFeature = (feedbackTrigger: AdminFeedbackViewModel) => AsyncResult<unknown, ApiServerError | SerializedError>;

export const useFeedbackEdit = (): [FeedbackEditFeature, boolean] => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const openModal = useModalPromise(FEEDBACK_EDIT_MODAL);
  const [editQuery] = adminCatalogFeedbackApi.endpoints.editFeedback.useMutation();
  const [approveFeeback] = coreModerationApi.endpoints.moderationApprove.useMutation();

  const runner = useCallback(
    (feedback: AdminFeedbackViewModel) => {
      setIsLoading(true);

      const observable = from(openModal({ feedback })).pipe(
        switchMap(({ date, ...data }) => from(resultFromRTK(editQuery({ id: feedback.id, createdAt: date.toISOString(), ...data })))),
        switchMap((r) => {
          if (r.isErr || !feedback.review?.moderationId) {
            return from(Promise.resolve(r));
          }
          return from(resultFromRTK(approveFeeback({ id: feedback.review.moderationId })));
        }),
        tap((res) => res.tap(() => toast('success', 'Отзыв отредактирован!'))),
        finalize(() => {
          setIsLoading(false);
        }),
        catchError(() => EMPTY),
        defaultIfEmpty(Result.Ok(null)),
      );

      return lastValueFrom(observable);
    },
    [approveFeeback, editQuery, openModal, toast],
  );

  return [runner, isLoading];
};
